import styled from "@emotion/styled";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { ReactComponent as ArrowUpIcon } from "Components/_Shared/Icons/Assets/arrow-up.svg";
import type { AssetAllocationFields as AssetAllocationFieldsType } from "Forms/Schemas/GoalsFulfillment/helpers";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import { type FieldValues, useFormContext } from "react-hook-form";
import { AssetAllocationFields } from "./AssetAllocationFields";

type Props = {
  readonly onChange?: () => void;
  readonly defaultExpanded?: boolean;
};

const StyledAccordion = styled(Accordion)`
  ${({ theme }) => `
    padding: ${theme.spacing(Spacing.XL)};
    border: 1px solid ${theme.colors.borderLight};
    border-radius: ${theme.spacing(Radius.M)} !important;
    box-shadow: ${theme.shadows[0]} !important;
  `}

  & .MuiAccordionSummary-content, & .MuiAccordionSummary-content .Mui-expanded {
    margin: 0 !important;
  }

  & .MuiAccordion-heading > .MuiButtonBase-root {
    min-height: 0;
    padding: 0;
  }

  & .MuiButtonBase-root.Mui-expanded {
    min-height: 0;
  }

  & .MuiAccordionDetails-root {
    padding: 0
  }
`;

export const assetAllocationFields = ["equities", "bonds", "commodities", "realEstate", "cash", "other"] as const;

export const CharacteristicsAccordion = <T extends FieldValues & AssetAllocationFieldsType>({
  onChange,
  defaultExpanded,
}: PropsWithChildren<Props>) => {
  const { t } = useResource();

  const {
    formState: { errors },
  } = useFormContext<T>();

  const hasAnyAllocationFieldsError = assetAllocationFields.some((field) => !!errors[field as keyof T]);
  const [expanded, setExpanded] = useState<boolean>(!!defaultExpanded || hasAnyAllocationFieldsError);

  useEffect(() => {
    if (defaultExpanded) {
      setExpanded(true);
    }
  }, [defaultExpanded]);

  const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (!isExpanded && hasAnyAllocationFieldsError) {
      return;
    }

    setExpanded(isExpanded);
    onChange?.();
  };

  useEffect(() => {
    if (hasAnyAllocationFieldsError) {
      setExpanded(true);
    }
  }, [hasAnyAllocationFieldsError]);

  return (
    <StyledAccordion expanded={expanded} onChange={handleAccordionChange}>
      <AccordionSummary
        expandIcon={
          hasAnyAllocationFieldsError ? (
            <WarningAmberIcon color="error" sx={{ width: "20px" }} />
          ) : (
            <ArrowUpIcon style={{ transform: "rotate(180deg)" }} />
          )
        }
        sx={{
          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
            transform: hasAnyAllocationFieldsError ? "none" : "rotate(180deg)",
          },
        }}
      >
        <Typography variant="subtitle1" fontWeight={500} lineHeight={1}>
          {t(Resources.AnalysisDetail.GoalsFulfillment.Characteristics)}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Stack>
          {hasAnyAllocationFieldsError && (
            <Flex justifyContent={"flex-end"}>
              <Typography variant="caption" color={"error"}>
                {t(Resources.Validation.AssetAllocationSum)}
              </Typography>
            </Flex>
          )}
          <AssetAllocationFields />
        </Stack>
      </AccordionDetails>
    </StyledAccordion>
  );
};
